import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Branch, LoginResponse, Role } from "@probetalab/contracts";
import { ApiError, get, post } from "./api.js";
import { applyLabBranding, rememberLab } from "./labBranding.js";

/** Session state: who is logged in, which branch is active. Loaded once from GET /v1/me. */
export interface SessionState {
  status: "loading" | "anonymous" | "ready";
  me: LoginResponse | null;
  branchId: string | null;
  branch: Branch | null;
  /** True until the user has picked a branch on this device (the picker screen shows once). */
  branchChosen: boolean;
  setBranchId: (id: string) => void;
  /** Role check: roles are additive (Químico ⊇ Recepción, Administrador ⊇ all). */
  can: (minimum: Role) => boolean;
  /** True for liveratti support staff (ADR 0017): the shell shows the support banner and the laboratory switcher. */
  isOperator: boolean;
  /** Operators only: enter another laboratory. The session view is replaced; the branch is chosen again. */
  switchTenant: (tenantId: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);
const BRANCH_KEY = "probeta.branch_id";
export const ROLE_RANK: Record<Role, number> = { recepcion: 1, quimico: 2, administrador: 3 };

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState["status"]>("loading");
  const [me, setMe] = useState<LoginResponse | null>(null);
  const [branchId, setBranchIdState] = useState<string | null>(localStorage.getItem(BRANCH_KEY));

  const apply = useCallback((data: LoginResponse | null) => {
    setMe(data);
    setStatus(data ? "ready" : "anonymous");
    if (data) {
      const stored = localStorage.getItem(BRANCH_KEY);
      const valid = data.branches.some((b) => b.id === stored);
      setBranchIdState(valid ? stored : null);
      if (!valid) localStorage.removeItem(BRANCH_KEY);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await get("me"));
    } catch (e) {
      if (e instanceof ApiError && (e.problem.status === 401 || e.problem.status === 403)) apply(null);
      else {
        // Network failure: keep the user on the login screen with a message rather than spinning forever.
        apply(null);
      }
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The tab shows the laboratory (name and logo) while signed in, Probeta Lab otherwise.
  useEffect(() => {
    if (status !== "loading") applyLabBranding(me);
    // A platform operator moves between laboratories: the device does not become any lab's.
    if (me && me.operator === undefined) void rememberLab(me);
  }, [status, me]);

  const value = useMemo<SessionState>(() => {
    const branch = me?.branches.find((b) => b.id === branchId) ?? null;
    const role = me?.user.role;
    return {
      status,
      me,
      branchId,
      branch,
      branchChosen: branch !== null,
      setBranchId: (id) => {
        localStorage.setItem(BRANCH_KEY, id);
        setBranchIdState(id);
      },
      can: (minimum) => (role ? ROLE_RANK[role] >= ROLE_RANK[minimum] : false),
      isOperator: me?.operator !== undefined,
      switchTenant: async (tenantId) => {
        localStorage.removeItem(BRANCH_KEY);
        apply(await post("operatorTenant", { tenant_id: tenantId }));
      },
      login: async (email, password) => apply(await post("login", { email, password })),
      logout: async () => {
        try {
          await post("logout");
        } finally {
          apply(null);
        }
      },
      refresh,
    };
  }, [status, me, branchId, apply, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}

/** The active branch, guaranteed inside the shell (the router redirects to the picker otherwise). */
export function useBranch(): Branch {
  const { branch } = useSession();
  if (!branch) throw new Error("useBranch outside an active branch");
  return branch;
}
